import { datadogLogs } from '@datadog/browser-logs';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { keysIn } from 'lodash';
import { FormState } from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  TAddressTypeDE,
  TApplicantDetailsTypeDE,
  TSignupFormDataDE,
} from '../NewSignupFormDE.types';

export const useHandleSubmitError = () => {
  const { trackEvent } = useAmplitude();
  const { introducerId } = useIntroducerContext();

  // Handle validation errors on form submit
  const handleErrorTracking = (
    error: FormState<TSignupFormDataDE>['errors'],
    stateKey?: string,
  ) => {
    if (!error) {
      return;
    }

    const trackError = (error?: string) => {
      trackEvent(AMPLITUDE_EVENTS.newApplicationSubmissionErrored, {
        'Error message': error!,
      });

      datadogLogs.logger.error('Failed to submit application', {
        createError: error,
        stateKey: stateKey,
        introducerId,
      });
    };

    const errorKeys = keysIn(error) as (keyof TSignupFormDataDE)[];

    errorKeys.forEach((errorKey) => {
      // Track personal details errors
      if (errorKey === 'guarantor') {
        (error[errorKey] || []).forEach?.((person) => {
          const personKeys = keysIn(
            person,
          ) as (keyof TApplicantDetailsTypeDE)[];

          if (person) {
            // Track address errors
            personKeys.forEach((personKey) => {
              if (personKey === 'residentialAddresses') {
                person.residentialAddresses?.forEach?.((address) => {
                  const addressKeys = keysIn(
                    address,
                  ) as (keyof TAddressTypeDE)[];
                  if (address) {
                    addressKeys.forEach((addressKey) => {
                      const errorMessage = address[addressKey]?.message;
                      trackError(errorMessage);
                    });
                  }
                });
              }
              const errorMessage = person[personKey]?.message;
              trackError(errorMessage);
            });
          }
        });
      }

      if (errorKey === 'applicant') {
        const personKeys = keysIn(
          error[errorKey],
        ) as (keyof TApplicantDetailsTypeDE)[];

        if (error[errorKey]) {
          // Track address errors
          personKeys.forEach((personKey) => {
            if (personKey === 'residentialAddresses') {
              error[errorKey]?.residentialAddresses?.forEach?.((address) => {
                const addressKeys = keysIn(address) as (keyof TAddressTypeDE)[];
                if (address) {
                  addressKeys.forEach((addressKey) => {
                    const errorMessage = address[addressKey]?.message;
                    trackError(errorMessage);
                  });
                }
              });
            }
            const errorMessage = error[errorKey]?.[personKey]?.message;
            trackError(errorMessage);
          });
        }
      }

      // Track other field errors
      else {
        trackError(error[errorKey]?.message);
      }
    });
  };
  return { handleErrorTracking };
};
